import { IOption } from 'app/components'
import { FornecedorDto, FornecedorEdicao, TipoFornecedor } from './_models'

export type ValoresFornecedor = {
  nome: string
  nomeFantasia: string
  cnpj: string
  inscricaoEstadual: string
  email: string
  telefone: string
  site: string
  contatoNome: string
  contatoTelefone: string
  contatoEmail: string
  tipos: IOption<TipoFornecedor>[]
  observacoes: string
  ativo: boolean
  logo: File | null
}

export const somenteDigitos = (valor?: string | null) => (valor ?? '').replace(/\D/g, '')

export const formataCnpj = (cnpj?: string | null) => {
  const d = somenteDigitos(cnpj)
  if (d.length !== 14) return cnpj ?? ''
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

/** Sem nome de contato, o backend grava a razão social: o campo volta vazio para seguir o nome. */
const nomeDoContato = (view?: FornecedorEdicao) => {
  const nome = view?.contato?.nome ?? ''
  return nome === view?.nome ? '' : nome
}

export const valoresIniciaisFornecedor = (
  view: FornecedorEdicao | undefined,
  opcaoTipo: (tipo: TipoFornecedor) => IOption<TipoFornecedor>
): ValoresFornecedor => ({
  nome: view?.nome ?? '',
  nomeFantasia: view?.nomeFantasia ?? '',
  cnpj: formataCnpj(view?.cnpj),
  inscricaoEstadual: view?.inscricaoEstadual ?? '',
  email: view?.email ?? '',
  telefone: view?.telefone ?? '',
  site: view?.site ?? '',
  contatoNome: nomeDoContato(view),
  contatoTelefone: view?.contato?.telefoneContato ?? '',
  contatoEmail: view?.contato?.email ?? '',
  tipos: (view?.tipos ?? []).map(opcaoTipo),
  observacoes: view?.observacoes ?? '',
  ativo: view?.ativo ?? true,
  logo: null,
})

/**
 * Corpo do POST/PUT do fornecedor. No PUT, `null`/ausente mantém o valor salvo e só `''` limpa,
 * por isso os textos vão sempre como string (vazia = limpar). CNPJ só com dígitos.
 */
export const paraDtoFornecedor = (valores: ValoresFornecedor): FornecedorDto => ({
  nome: valores.nome.trim(),
  nomeFantasia: valores.nomeFantasia.trim(),
  cnpj: somenteDigitos(valores.cnpj),
  inscricaoEstadual: valores.inscricaoEstadual.trim(),
  email: valores.email.trim(),
  telefone: valores.telefone.trim(),
  site: valores.site.trim(),
  contatoNome: valores.contatoNome.trim(),
  contatoTelefone: valores.contatoTelefone.trim(),
  contatoEmail: valores.contatoEmail.trim(),
  tipos: valores.tipos.map((opcao) => opcao.value),
  observacoes: valores.observacoes.trim(),
  ativo: valores.ativo,
})

/** Dígitos verificadores do CNPJ numérico (o alfanumérico da Receita ainda não é aceito no backend). */
export const cnpjValido = (cnpj?: string | null) => {
  const d = somenteDigitos(cnpj)
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false
  const digito = (base: string) => {
    const pesos =
      base.length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    const soma = pesos.reduce((acc, peso, i) => acc + Number(base[i]) * peso, 0)
    const resto = soma % 11
    return resto < 2 ? 0 : 11 - resto
  }
  const d1 = digito(d.slice(0, 12))
  const d2 = digito(d.slice(0, 12) + d1)
  return d.endsWith(`${d1}${d2}`)
}
